// Helpers of the universal context menu (no React state in here)

// Where the browser's own menu is more useful than ours: fields (spell check, paste), selected
// text (copy, search), and anything right-clicked with Alt/Option held.
const NATIVE_MENU_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-native-context-menu]';

export const shouldUseNativeMenu = (event) => {
  if (!event) return false;
  if (event.altKey) return true;
  const target = event.target instanceof Element ? event.target : null;
  if (target && (target.isContentEditable || target.closest(NATIVE_MENU_SELECTOR))) return true;
  const selection = typeof window !== "undefined" ? window.getSelection?.() : null;
  if (selection && !selection.isCollapsed && selection.toString().trim()) {
    const scope = event.currentTarget instanceof Node ? event.currentTarget : target;
    const node = selection.anchorNode;
    if (!scope || !node || scope.contains(node)) return true;
  }
  return false;
};

// A contextmenu fired by the keyboard (ContextMenu key, Shift+F10) has no pointer position
export const isKeyboardContextMenu = (event) => {
  const native = event?.nativeEvent || event;
  if (!native) return false;
  if (native.pointerType === "") return true;
  return native.clientX === 0 && native.clientY === 0;
};

// Point where a menu opened from the keyboard appears: top-left area of the element
export const pointFromElement = (element) => {
  if (!element?.getBoundingClientRect) return { x: 0, y: 0 };
  const rect = element.getBoundingClientRect();
  return {
    x: Math.round(rect.left + Math.min(16, rect.width / 2)),
    y: Math.round(rect.top + Math.min(rect.height, 32)),
  };
};

const resolve = (value) => (typeof value === "function" ? value() : value);

const cleanDividers = (list) => {
  const out = [];
  list.forEach((item) => {
    if (item.divider) {
      if (out.length && !out[out.length - 1].divider) out.push(item);
      return;
    }
    out.push(item);
  });
  while (out.length && out[out.length - 1].divider) out.pop();
  return out;
};

/**
 * Drops hidden and falsy entries (so `cond && item` works), submenus left empty, headers with
 * nothing under them, and leading, trailing or repeated dividers. Gives every entry an id.
 * `children` may be an array or a function returning one (evaluated when the menu opens).
 */
export const normalizeItems = (items) => {
  const list = resolve(items);
  if (!Array.isArray(list)) return [];
  const kept = [];
  list.forEach((raw, index) => {
    if (!raw || raw.hidden) return;
    if (raw.divider) {
      kept.push({ ...raw, id: raw.id ?? `divider-${index}` });
      return;
    }
    let item = { ...raw, id: raw.id ?? `item-${index}` };
    if (raw.children) {
      const children = normalizeItems(raw.children);
      if (!children.length) return;
      item = { ...item, children };
    }
    kept.push(item);
  });
  const withoutEmptyHeaders = cleanDividers(kept).filter((item, index, all) => {
    if (!item.header) return true;
    const next = all[index + 1];
    return next && !next.divider && !next.header;
  });
  return cleanDividers(withoutEmptyHeaders);
};

/**
 * Merges several props objects; handlers with the same name (onKeyDown, onPointerDown...) are all
 * called, in order. For a draggable card: mergeProps(menu.bind(items), attributes, listeners).
 */
export const mergeProps = (...sources) =>
  sources.filter(Boolean).reduce((merged, source) => {
    Object.entries(source).forEach(([name, value]) => {
      const previous = merged[name];
      merged[name] =
        /^on[A-Z]/.test(name) && typeof previous === "function" && typeof value === "function"
          ? (...args) => {
              previous(...args);
              value(...args);
            }
          : value;
    });
    return merged;
  }, {});
